import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Body5 } from '@ui';
import { useDispatch } from 'react-redux';
import { tableBuilderActions } from '@constants/actionTypes';

function convertRowIndexToLetter(rowIndex) {
  return String.fromCharCode(65 + rowIndex);
}

const TableToolbarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};

  min-height: 48px;
`;

const CellFormattingButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;

  height: 32px;
  width: 32px;

  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  background: ${({ isActive, theme }) => (isActive ? theme.themeColors.border : theme.themeColors.componentNoOpacity)};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border: 1px solid ${({ theme }) => theme.themeColors.text};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
    fill: ${({ theme }) => theme.themeColors.text};
    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;

const CellFormattingButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const CellValueInput = styled.input`
  flex: 1;
  max-width: 400px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font-size: 14px;
  font-family: inherit;
  transition: border 0.2s;

  &:focus {
    outline: none;
    border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const TableToolbar = ({ activeCell, activeCellMetadata, data }) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState('');

  const isBold = activeCellMetadata?.bold || false;
  const isItalic = activeCellMetadata?.italic || false;
  const isUnderline = activeCellMetadata?.underline || false;
  const isStrike = activeCellMetadata?.strike || false;

  // Get current cell data from data
  const currentCell = activeCell
    ? data?.find((cell) => cell.rowIndex === activeCell.rowIndex && cell.columnIndex === activeCell.columnIndex)
    : null;

  const currentCellValue = currentCell?.value || '';
  const currentCellFormula = currentCell?.formula || '';

  // Show formula if it exists, otherwise show value
  const displayValue = currentCellFormula || currentCellValue;

  // Update input value when active cell changes
  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue, activeCell?.rowIndex, activeCell?.columnIndex]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    if (!activeCell) return;

    // Only dispatch if value has changed
    if (inputValue !== displayValue) {
      dispatch({
        type: tableBuilderActions.updateCell,
        payload: {
          rowIndex: activeCell.rowIndex,
          columnIndex: activeCell.columnIndex,
          value: inputValue,
        },
      });
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur(); // Trigger blur to save
    }
  };

  const handleToggleBold = () => {
    if (!activeCell) return;

    dispatch({
      type: tableBuilderActions.toggleCellBold,
      payload: {
        rowIndex: activeCell.rowIndex,
        columnIndex: activeCell.columnIndex,
      },
    });
  };

  const handleToggleItalic = () => {
    if (!activeCell) return;

    dispatch({
      type: tableBuilderActions.toggleCellItalic,
      payload: {
        rowIndex: activeCell.rowIndex,
        columnIndex: activeCell.columnIndex,
      },
    });
  };

  const handleToggleUnderline = () => {
    if (!activeCell) return;

    dispatch({
      type: tableBuilderActions.toggleCellUnderline,
      payload: {
        rowIndex: activeCell.rowIndex,
        columnIndex: activeCell.columnIndex,
      },
    });
  };

  const handleToggleStrike = () => {
    if (!activeCell) return;

    dispatch({
      type: tableBuilderActions.toggleCellStrike,
      payload: {
        rowIndex: activeCell.rowIndex,
        columnIndex: activeCell.columnIndex,
      },
    });
  };

  return (
    <TableToolbarWrapper>
      <div>
        <Body5>
          {activeCell
            ? `Cell: ${convertRowIndexToLetter(activeCell?.columnIndex)}${activeCell?.rowIndex + 1}`
            : 'No cell selected'}
        </Body5>
      </div>
      {activeCell && (
        <CellValueInput
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          placeholder="Enter cell value"
        />
      )}
      {activeCell && (
        <CellFormattingButtons>
          <CellFormattingButton onClick={handleToggleBold} disabled={!activeCell} isActive={isBold}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M13.5,15.5H10V12.5H13.5A1.5,1.5 0 0,1 15,14A1.5,1.5 0 0,1 13.5,15.5M10,6.5H13A1.5,1.5 0 0,1 14.5,8A1.5,1.5 0 0,1 13,9.5H10M15.6,10.79C16.57,10.11 17.25,9 17.25,8C17.25,5.74 15.5,4 13.25,4H7V18H14.04C16.14,18 17.75,16.3 17.75,14.21C17.75,12.69 16.89,11.39 15.6,10.79Z" />
            </svg>
          </CellFormattingButton>
          <CellFormattingButton onClick={handleToggleItalic} disabled={!activeCell} isActive={isItalic}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ height: '20px', width: '20px' }}>
              <path d="M10,4V7H12.21L8.79,15H6V18H14V15H11.79L15.21,7H18V4H10Z" />
            </svg>
          </CellFormattingButton>
          <CellFormattingButton onClick={handleToggleUnderline} disabled={!activeCell} isActive={isUnderline}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ height: '18px', width: '18px' }}>
              <path d="M5,21H19V19H5V21M12,17A6,6 0 0,0 18,11V3H15.5V11A3.5,3.5 0 0,1 12,14.5A3.5,3.5 0 0,1 8.5,11V3H6V11A6,6 0 0,0 12,17Z" />
            </svg>
          </CellFormattingButton>
          <CellFormattingButton onClick={handleToggleStrike} disabled={!activeCell} isActive={isStrike}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ height: '16px', width: '16px' }}>
              <path d="M23,12V14H18.61C19.61,16.14 19.56,22 12.38,22C4.05,22.05 4.37,15.5 4.37,15.5L8.34,15.55C8.37,18.92 11.5,18.92 12.12,18.88C12.76,18.83 15.15,18.84 15.34,16.5C15.42,15.41 14.32,14.58 13.12,14H1V12H23M19.41,7.89L15.43,7.86C15.43,7.86 15.6,5.09 12.15,5.08C8.7,5.06 9,7.28 9,7.56C9.04,7.84 9.34,9.22 12,9.88H5.71C5.71,9.88 2.22,3.15 10.74,2C19.45,0.8 19.43,7.91 19.41,7.89Z" />
            </svg>
          </CellFormattingButton>
        </CellFormattingButtons>
      )}
    </TableToolbarWrapper>
  );
};
